/* ─────────────────────────────────────────────────────────────────────────
   The product — a white instrument enclosure the board is built into.

   It is printed (clip plane rising through the tray), receives the board,
   gets its lid, powers on, is validated, and later explodes into the layers
   the Capabilities chapter lights up. Origin: centre of the tray's floor.
   ───────────────────────────────────────────────────────────────────────── */
import * as THREE from 'three';
import { roundedRect } from './board';

export const ENC = { W: 1.04, D: 0.74, H: 0.3, R: 0.1, WALL: 0.035, FLOOR: 0.03, LID: 0.065 };
export const LAYERS = 3;    // printed layers — one lap of the nozzle each, one per scroll
export const LAYER_H = ENC.H / LAYERS;

/* ── the print job ──────────────────────────────────────────────────────
   One schedule drives both the head (printer.js) and the part (here), so
   the plastic always appears exactly where the nozzle is.

   The whole tray goes down in LAYERS laps: the head runs once round the
   wall per layer and steps straight up to the next — no back-and-forth
   passes. On the first lap the floor spreads in under the head as it goes,
   so the base is laid by the same lap as the bottom of the wall. */
const { W: _W, D: _D, R: _R, WALL: _WL } = ENC;
const IN = { a: _W / 2 - _WL, b: _D / 2 - _WL, r: _R - _WL };   // the tray's inside

/* the wall's centreline, as a polyline measured by length */
const PERIM = (() => {
  const i = _WL / 2;
  const pts = roundedRect(-_W / 2 + i, -_D / 2 + i, _W / 2 - i, _D / 2 - i, _R - i, 0, 10);
  pts.push(pts[0].clone());
  const lens = [0];
  for (let k = 1; k < pts.length; k++) lens.push(lens[k - 1] + pts[k].distanceTo(pts[k - 1]));
  return { pts, lens, L: lens[lens.length - 1] };
})();
export function perimeterAt(u, out = new THREE.Vector3()) {
  const { pts, lens, L } = PERIM;
  const d = Math.min(Math.max(u, 0), 1) * L; let k = 1;
  while (k < lens.length - 1 && lens[k] < d) k++;
  const t = (d - lens[k - 1]) / Math.max(1e-6, lens[k] - lens[k - 1]);
  return out.lerpVectors(pts[k - 1], pts[k], t);
}

/* p (0..1) → which layer and how far round its lap the head is */
export function printJob(p) {
  const t = Math.min(Math.max(p, 0), 0.99999) * LAYERS;
  const layer = Math.floor(t), f = t - layer;
  return { layer, f, h: layer * LAYER_H, floor: layer === 0, perim: f, infill: layer === 0 ? f : 0 };
}

/* where the nozzle tip is for a job, in the part's own frame */
export function nozzleFor(job, out = new THREE.Vector3()) {
  return perimeterAt(job.perim, out).setY(job.h + LAYER_H);
}

/* fresh plastic glows where it leaves the nozzle and cools behind it */
function hotTrail(mat, uniforms, along) {
  mat.onBeforeCompile = sh => {
    Object.assign(sh.uniforms, uniforms);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
        varying float vAlong;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vAlong = ${along};`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform float uHead, uLen;
        varying float vAlong;`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        float behind = (uHead - vAlong) * uLen;
        float heat = (1.0 - smoothstep(0.0, 0.11, behind)) * step(0.0, behind + 0.004);
        totalEmissiveRadiance += vec3(1.0, 0.32, 0.06) * heat * 1.6;
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0, 0.55, 0.3), heat * 0.6);`);
  };
  return mat;
}

function rrShape(w, d, r) {
  const s = new THREE.Shape();
  const x = -w / 2, y = -d / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + d - r); s.quadraticCurveTo(x + w, y + d, x + w - r, y + d);
  s.lineTo(x + r, y + d); s.quadraticCurveTo(x, y + d, x, y + d - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}
function slab(w, d, r, h, bevel = 0.012, hole = null) {
  const s = rrShape(w - bevel * 2, d - bevel * 2, Math.max(0.005, r - bevel));
  if (hole) s.holes.push(rrShape(hole[0], hole[1], hole[2]));
  const g = new THREE.ExtrudeGeometry(s, {
    depth: Math.max(0.001, h - bevel * 2), bevelEnabled: bevel > 0, bevelThickness: bevel,
    bevelSize: bevel, bevelSegments: 4, curveSegments: 10,
  });
  g.rotateX(-Math.PI / 2);
  g.translate(0, bevel, 0);
  return g;
}

export const PALETTE = {
  shell: () => new THREE.MeshPhysicalMaterial({ color: 0xf4f5f7, roughness: 0.42, metalness: 0, clearcoat: 0.35, clearcoatRoughness: 0.4 }),
  red: () => new THREE.MeshStandardMaterial({ color: 0xe0070b, roughness: 0.35, metalness: 0.1, emissive: 0x2a0000 }),
  graphite: () => new THREE.MeshStandardMaterial({ color: 0x24262b, roughness: 0.55, metalness: 0.3 }),
  steel: () => new THREE.MeshStandardMaterial({ color: 0xb9bdc4, roughness: 0.3, metalness: 0.9 }),
  glass: () => new THREE.MeshPhysicalMaterial({ color: 0x0b0c10, roughness: 0.08, metalness: 0, clearcoat: 1 }),
};

export class Enclosure {
  constructor() {
    const { W, D, H, R, WALL, FLOOR, LID } = ENC;
    this.root = new THREE.Group(); this.root.name = 'enclosure';
    this.shellMat = PALETTE.shell();
    /* layer lines: a printed part is banded at the layer pitch. Driven by a
       uniform so the banding is strong on the bed and settles to a trace. */
    this.stripe = { value: 0 };
    this.layerH = { value: ENC.H / LAYERS };
    this.striped = m => {
      m.onBeforeCompile = sh => {
        sh.uniforms.uStripe = this.stripe;
        sh.uniforms.uLayerH = this.layerH;
        sh.vertexShader = sh.vertexShader
          .replace('#include <common>', `#include <common>
            varying float vLayerY;`)
          .replace('#include <begin_vertex>', `#include <begin_vertex>
            vLayerY = position.y;`);
        sh.fragmentShader = sh.fragmentShader
          .replace('#include <common>', `#include <common>
            uniform float uStripe;
            uniform float uLayerH;
            varying float vLayerY;`)
          .replace('#include <dithering_fragment>', `#include <dithering_fragment>
            float lz = fract(vLayerY / uLayerH);
            float band = smoothstep(0.0, 0.18, lz) * smoothstep(1.0, 0.82, lz);
            gl_FragColor.rgb *= mix(1.0, mix(0.82, 1.03, band), uStripe);`);
      };
      return m;
    };
    this.striped(this.shellMat);
    this.printClip = new THREE.Plane(new THREE.Vector3(0, -1, 0), 10);
    this.shellMat.clippingPlanes = [this.printClip];
    this.shellMat.clipShadows = true;
    this.redMat = PALETTE.red();
    this.redMat.clippingPlanes = [this.printClip];

    /* layers (keys match content.js CAPABILITIES.layer) */
    this.layers = {};
    const layer = (k) => { const g = new THREE.Group(); g.name = k; this.layers[k] = g; return g; };

    // tray: floor + walls
    const base = layer('base');
    const floorMat = this.shellMat.clone();
    floorMat.clippingPlanes = [this.printClip];
    this.striped(floorMat);
    const floor = new THREE.Mesh(slab(W, D, R, FLOOR, 0.01), floorMat);
    base.add(floor);
    const feet = new THREE.Mesh(slab(W * 0.9, D * 0.86, R * 0.8, 0.012, 0.004), PALETTE.graphite());
    feet.position.y = -0.011; base.add(feet);

    const shell = layer('shell');
    const walls = new THREE.Mesh(slab(W, D, R, H - FLOOR, 0.012, [W - WALL * 2, D - WALL * 2, R - WALL]), this.shellMat);
    walls.position.y = FLOOR; shell.add(walls);
    // front accent band + status LEDs
    const band = new THREE.Mesh(new THREE.BoxGeometry(W * 0.5, 0.012, 0.004), this.redMat);
    band.position.set(0, H * 0.45, D / 2 + 0.001); shell.add(band);
    this.leds = [];
    for (let i = 0; i < 3; i++) {
      const m = new THREE.MeshStandardMaterial({ color: 0x333333, emissive: 0x000000, roughness: 0.3 });
      const led = new THREE.Mesh(new THREE.CircleGeometry(0.009, 16), m);
      led.position.set(W * 0.34 + i * 0.03, H * 0.45, D / 2 + 0.003); shell.add(led); this.leds.push(m);
    }
    // side vents
    for (let i = 0; i < 6; i++) {
      const v = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.012, 0.05), PALETTE.graphite());
      v.position.set(W / 2 + 0.001, H * 0.55, -0.15 + i * 0.06); shell.add(v);
    }

    // inner frame: standoffs + battery pack under the board
    const frame = layer('frame');
    const standMat = PALETTE.steel();
    [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([sx, sz]) => {
      const st = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.05, 16), standMat);
      st.position.set(sx * 0.37, FLOOR + 0.025, sz * 0.22); frame.add(st);
    });
    const batt = new THREE.Mesh(slab(0.5, 0.28, 0.03, 0.035, 0.006), PALETTE.graphite());
    batt.position.set(-0.12, FLOOR, 0.02); frame.add(batt);

    // where the board sits (board space origin = board top centre)
    this.seat = new THREE.Object3D();
    this.seat.position.set(0, FLOOR + 0.05 + 0.017, 0);
    const boardLayer = layer('board');
    boardLayer.add(this.seat);

    // lid + screen
    const lid = layer('lid');
    this.lidMat = PALETTE.shell();
    const lidMesh = new THREE.Mesh(slab(W, D, R, LID, 0.018), this.lidMat);
    lid.add(lidMesh);
    const lidBand = new THREE.Mesh(slab(W * 1.002, D * 1.002, R, 0.008, 0.002), PALETTE.red());
    lidBand.position.y = -0.004; lid.add(lidBand);

    const screen = layer('screen');
    this.canvas = document.createElement('canvas');
    this.canvas.width = 640; this.canvas.height = 400;
    this.ctx = this.canvas.getContext('2d');
    this.tex = new THREE.CanvasTexture(this.canvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = 4;
    this.screenMat = new THREE.MeshBasicMaterial({ map: this.tex, toneMapped: false });
    const bezel = new THREE.Mesh(slab(0.7, 0.46, 0.035, 0.006, 0.002), PALETTE.glass());
    screen.add(bezel);
    const scr = new THREE.Mesh(new THREE.PlaneGeometry(0.64, 0.4), this.screenMat);
    scr.rotation.x = -Math.PI / 2; scr.position.y = 0.0065; screen.add(scr);
    screen.position.set(0, LID, 0);
    lid.add(screen);
    this.lid = lid;
    this.lid.position.y = H;

    this.parts = [base, shell, frame, boardLayer, lid];
    this.parts.forEach(p => this.root.add(p));
    this.rest = new Map(this.parts.map(p => [p, p.position.clone()]));
    this.screenLayer = screen;

    /* finishing details belong to the assembled product, not the print: they
       stay hidden until the part comes off the bed */
    this.details = [band, ...shell.children.filter(o => o !== walls && o !== band), feet];

    /* The layer in progress, laid down exactly as far as the nozzle has got.
       The perimeter bead follows the same measured path the head does, and is
       squashed to one layer tall so it sits flush on the layer below. */
    const perimCurve = new THREE.Curve();
    perimCurve.getPoint = (t, out = new THREE.Vector3()) => perimeterAt(t, out);
    const beadR = WALL * 0.5;
    const beadGeo = new THREE.TubeGeometry(perimCurve, 320, beadR, 8, true);
    this.heat = { uHead: { value: 0 }, uLen: { value: PERIM.L } };
    this.bead = new THREE.Mesh(beadGeo, hotTrail(
      new THREE.MeshStandardMaterial({ color: 0xf4f5f7, roughness: 0.45 }), this.heat, 'uv.x'));
    this.bead.scale.y = LAYER_H / (beadR * 2);
    this.root.add(this.bead);
    this.beadCount = beadGeo.index.count;

    /* the floor, spreading in under the first lap */
    const inside = rrShape(IN.a * 2 + 0.004, IN.b * 2 + 0.004, IN.r);
    const infillGeo = new THREE.ExtrudeGeometry(inside, { depth: FLOOR, bevelEnabled: false, curveSegments: 10 });
    infillGeo.rotateX(-Math.PI / 2);
    this.infillClip = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
    const infillMat = this.shellMat.clone();
    infillMat.clippingPlanes = [this.infillClip];
    this.infill = new THREE.Mesh(infillGeo, infillMat);
    this.root.add(this.infill);

    this.state = { power: 0, mode: 'off', t: 0, data: null };
    this.drawScreen(0);
    this.root.traverse(o => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
  }

  /* 0..1 — how much of the tray exists. Finished layers are solid below the
     clip; the layer in progress is the bead (and, on the floor, the raster
     fill) laid exactly as far as the nozzle has got — see printJob(). */
  setPrint(p) {
    const printing = p > 0 && p < 1;
    const job = printJob(p);
    const h = p >= 1 ? 50 : job.h;
    this.root.updateWorldMatrix(true, false);
    const M = this.root.matrixWorld;
    this.printClip.copy(new THREE.Plane(new THREE.Vector3(0, -1, 0), h)).applyMatrix4(M);

    /* perimeter of the current layer */
    this.bead.visible = printing && job.perim > 0;
    this.bead.position.y = job.h + LAYER_H / 2;   // the squashed tube fills [h, h + layer]
    this.bead.geometry.setDrawRange(0, Math.max(6, Math.floor(this.beadCount * job.perim / 6) * 6));
    this.heat.uHead.value = job.perim;

    /* first lap: the floor fills in front to back as the head goes round */
    this.infill.visible = printing && job.floor && job.infill > 0;
    if (this.infill.visible) {
      this.infillClip.copy(new THREE.Plane(new THREE.Vector3(0, 0, -1), -IN.b + IN.b * 2 * job.infill)).applyMatrix4(M);
    }

    this.layers.base.visible = this.layers.shell.visible = p > 0;
    this.details.forEach(o => { o.visible = p >= 1; });
    this.layers.frame.visible = p >= 1;
    return h;
  }

  /* lift: how far above its seat the lid hovers (explode() applies it) */
  setLid(v, lift) {
    this.lid.visible = v > 0;
    this.lidLift = lift;
  }

  setLeds(k, ok) {
    this.leds.forEach((m, i) => {
      const on = k > (i + 1) / 4;
      const c = !on ? 0x000000 : ok && i === 2 ? 0x19e36b : i === 0 ? 0xff1a1a : 0xffffff;
      m.emissive.setHex(c); m.emissiveIntensity = on ? 2 : 0;
    });
  }

  /* screen content: mode 'off' | 'boot' | 'live' | 'test' | 'pass' */
  drawScreen(time, mode = 'off', k = 0, data = {}) {
    const c = this.ctx, W = 640, H = 400;
    c.fillStyle = '#07080b'; c.fillRect(0, 0, W, H);
    if (mode === 'off') { this.tex.needsUpdate = true; return; }
    const mono = '600 20px "JetBrains Mono", monospace';
    if (mode === 'boot') {
      c.globalAlpha = Math.min(1, k * 3);
      c.fillStyle = '#fff'; c.font = '800 64px Inter, sans-serif'; c.textAlign = 'center';
      c.fillText('ZONE', W / 2 - 40, H / 2); c.fillStyle = '#ff1a1a'; c.fillText('-X', W / 2 + 92, H / 2);
      c.fillStyle = 'rgba(255,255,255,0.5)'; c.font = mono; c.fillText('SYSTEM INITIALIZING', W / 2, H / 2 + 50);
      c.fillStyle = 'rgba(255,255,255,0.12)'; c.fillRect(170, H / 2 + 80, 300, 4);
      c.fillStyle = '#ff1a1a'; c.fillRect(170, H / 2 + 80, 300 * k, 4);
      c.globalAlpha = 1; this.tex.needsUpdate = true; return;
    }
    // live UI
    c.textAlign = 'left';
    c.fillStyle = 'rgba(255,255,255,0.45)'; c.font = mono; c.fillText('ZX-CORE · NODE 01', 30, 44);
    const status = mode === 'pass' ? 'PASS' : mode === 'test' ? 'TESTING' : 'ONLINE';
    c.fillStyle = mode === 'pass' ? '#19e36b' : mode === 'test' ? '#ffb020' : '#ff1a1a';
    c.textAlign = 'right'; c.fillText('● ' + status, W - 30, 44); c.textAlign = 'left';
    const tiles = [['TEMP', data.temp || '36.4', '°C'], ['SIGNAL', data.signal || '98', '%'], ['POWER', data.power || '12.2', 'W']];
    tiles.forEach(([l, v, u], i) => {
      const x = 30 + i * 200;
      c.fillStyle = 'rgba(255,255,255,0.06)'; c.fillRect(x, 70, 180, 120);
      c.fillStyle = 'rgba(255,255,255,0.5)'; c.font = '600 16px "JetBrains Mono", monospace'; c.fillText(l, x + 14, 98);
      c.fillStyle = '#fff'; c.font = '700 48px Inter, sans-serif'; c.fillText(v, x + 14, 160);
      c.fillStyle = 'rgba(255,255,255,0.5)'; c.font = '600 18px Inter, sans-serif'; c.fillText(u, x + 14 + c.measureText(v).width + 2 + 0, 160);
    });
    // waveform
    c.strokeStyle = '#ff1a1a'; c.lineWidth = 3; c.beginPath();
    for (let x = 0; x <= 580; x += 4) {
      const t = x / 580 * 12 + time * 3;
      const y = 300 + Math.sin(t) * 22 * Math.sin(t * 0.37 + 1) + (Math.abs(((t * 0.8) % 6) - 3) < 0.15 ? -45 : 0);
      x === 0 ? c.moveTo(30 + x, y) : c.lineTo(30 + x, y);
    }
    c.stroke();
    if (mode === 'pass') {
      c.fillStyle = 'rgba(25,227,107,0.14)'; c.fillRect(0, 0, W, H);
      c.fillStyle = '#19e36b'; c.font = '800 40px Inter, sans-serif'; c.textAlign = 'center'; c.fillText('✓ VALIDATED', W / 2, 370);
    }
    this.tex.needsUpdate = true;
  }

  /* how strongly the printed layer lines show, 0..1 */
  setStripe(v) { this.stripe.value = v; }

  /* Capabilities: pull the layers apart. */
  explode(k) {
    const gap = { base: -0.34, shell: -0.12, frame: 0.1, board: 0.36, lid: 0.72 };
    this.parts.forEach(p => {
      const r = this.rest.get(p);
      p.position.y = r.y + (gap[p.name] || 0) * k + (p === this.lid ? this.lidLift || 0 : 0);
    });
    this.screenLayer.position.y = ENC.LID + 0.18 * k;
  }

  /* Glow a layer red (capability hover). */
  highlight(key, amt) {
    Object.entries(this.layers).forEach(([k, g]) => {
      const a = k === key ? amt : 0;
      g.traverse(o => {
        if (!o.isMesh || !o.material.emissive) return;
        if (!o.userData.baseEmissive) o.userData.baseEmissive = o.material.emissive.clone();
        o.material.emissive.copy(o.userData.baseEmissive).lerp(new THREE.Color(0xff1a1a), a * 0.55);
      });
    });
  }
}
